import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { 
  Droplets, 
  Heart, 
  MessageSquare, 
  QrCode, 
  MapPin, 
  ChevronDown, 
  Sparkles,
  ArrowRight,
  Users,
  Share2,
  Gift,
  ExternalLink,
  X
} from 'lucide-react';

// Premium color palette
const colors = {
  gold: '#D4AF37',
  goldLight: '#F5E6B8',
  goldDark: '#A68B28',
  black: '#050505',
  darkGray: '#0A0A0A',
  white: '#FFFFFF'
};

// Floating Crystal Component with Glow
const FloatingCrystal = ({ className = "" }) => (
  <motion.div 
    animate={{ 
      y: [0, -15, 0],
      rotate: [0, 1, 0]
    }}
    transition={{ 
      duration: 6, 
      repeat: Infinity, 
      ease: "easeInOut" 
    }}
    className={`relative ${className}`}
  >
    <img 
      src="/landing-assets/cristal.png" 
      alt="Pont10 Love & Thanks Water Crystal"
      className="w-full h-full object-contain"
      style={{
        filter: 'drop-shadow(0 0 60px rgba(212, 175, 55, 0.4))'
      }}
    />
    {/* Animated Glow Ring */}
    <motion.div 
      animate={{ 
        scale: [1, 1.1, 1],
        opacity: [0.3, 0.5, 0.3]
      }}
      transition={{ duration: 4, repeat: Infinity }}
      className="absolute inset-0 rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(212,175,55,0.2) 0%, transparent 70%)',
        filter: 'blur(40px)'
      }}
    />
  </motion.div>
);

// Glassmorphism Card
const GlassCard = ({ children, className = "", hover = true }) => (
  <motion.div
    whileHover={hover ? { y: -8, scale: 1.02 } : {}}
    transition={{ duration: 0.4, ease: "easeOut" }}
    className={`
      relative overflow-hidden rounded-3xl
      bg-white/[0.03] backdrop-blur-xl
      border border-white/10
      ${hover ? 'cursor-pointer hover:border-[#D4AF37]/40' : ''}
      transition-all duration-500
      ${className}
    `}
  >
    {/* Subtle Shine Effect */}
    <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-700">
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, transparent 40%, rgba(212,175,55,0.05) 50%, transparent 60%)'
        }}
      />
    </div>
    {children}
  </motion.div>
);

// Section Wrapper
const Section = ({ children, className = "", id = "", dark = false }) => (
  <section 
    id={id} 
    className={`
      min-h-screen flex flex-col items-center justify-center 
      px-6 py-24 md:py-32 relative overflow-hidden
      ${dark ? 'bg-[#030303]' : 'bg-[#050505]'}
      ${className}
    `}
  >
    {children}
  </section>
);

// Animated Text Reveal
const RevealText = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    className={className}
  >
    {children}
  </motion.div>
);

// Main Landing Component
export default function LoveAndThanksWater() {
  const [isLoaded, setIsLoaded] = useState(false);
  const { scrollYProgress } = useScroll();
  const heroRef = useRef(null);
  
  // Parallax transforms
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.2], [1, 0.9]);
  const crystalY = useTransform(scrollYProgress, [0, 0.5], [0, -150]);
  
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const featureCards = [
    { 
      icon: Heart, 
      title: "Gratitud", 
      desc: "El agua como vehículo de emociones positivas",
      detail: "Inspirado en la investigación de Masaru Emoto sobre cristales de agua"
    },
    { 
      icon: Droplets, 
      title: "Minimalismo", 
      desc: "Belleza oculta dentro de la simplicidad",
      detail: "Diseño puro que conecta con lo esencial"
    },
    { 
      icon: MapPin, 
      title: "Besalú", 
      desc: "Conexión profunda con el territorio",
      detail: "El puente medieval como símbolo icónico"
    },
    { 
      icon: Sparkles, 
      title: "Ritual", 
      desc: "Transformar lo cotidiano en memorable",
      detail: "Un acto simple convertido en experiencia"
    }
  ];

  const experienceSteps = [
    { num: "01", title: "Objeto Icónico", desc: "Asociado a la identidad de Pont10" },
    { num: "02", title: "Compartible", desc: "Elemento visual para turistas e invitados" },
    { num: "03", title: "Entrada Digital", desc: "Código QR al ecosistema digital" },
    { num: "04", title: "Souvenir", desc: "Futuro producto para visitantes" }
  ];

  const interactionPoints = [
    "Escanea para descubrir la filosofía",
    "Deja un mensaje para el agua de Besalú",
    "Conecta con la comunidad global",
    "Participa en el ritual del agua"
  ];

  // Generate PDF function - Mobile compatible
  const generatePDF = () => {
    const pdfContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Love & Thanks Water - Pont10 Besalú '26</title>
        <link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Inter', sans-serif; 
            background: #050505; 
            color: #ffffff; 
            padding: 30px;
            max-width: 800px;
            margin: 0 auto;
          }
          .gold { color: #D4AF37; }
          .serif { font-family: 'Cinzel', serif; }
          h1 { font-size: 42px; font-weight: 300; margin-bottom: 10px; }
          h2 { font-size: 26px; font-weight: 400; margin: 35px 0 18px; }
          h3 { font-size: 16px; font-weight: 600; margin: 18px 0 10px; text-transform: uppercase; letter-spacing: 2px; }
          p { font-size: 14px; line-height: 1.8; opacity: 0.8; margin-bottom: 12px; }
          
          /* Cover Page */
          .cover { 
            min-height: 100vh; 
            display: flex; 
            flex-direction: column; 
            justify-content: center; 
            align-items: center; 
            text-align: center;
            padding: 40px 20px;
            page-break-after: always;
          }
          .cover-logo {
            width: 120px;
            height: 120px;
            margin-bottom: 50px;
            opacity: 0.9;
          }
          .cover-title {
            font-size: 72px;
            font-weight: 300;
            margin-bottom: 20px;
            letter-spacing: 2px;
          }
          .cover-subtitle {
            font-size: 20px;
            text-transform: uppercase;
            letter-spacing: 6px;
            opacity: 0.7;
            margin-bottom: 60px;
          }
          .cover-main {
            font-size: 56px;
            font-weight: 300;
            line-height: 1.2;
            margin-bottom: 30px;
          }
          .cover-desc {
            max-width: 500px;
            font-size: 16px;
            opacity: 0.6;
            line-height: 1.8;
          }
          
          .content { padding-top: 20px; }
          .section { margin-bottom: 40px; padding-bottom: 25px; border-bottom: 1px solid rgba(255,255,255,0.05); }
          .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin-top: 18px; }
          .card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; padding: 16px; }
          .card h4 { font-size: 13px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; }
          .card p { font-size: 11px; opacity: 0.6; margin: 0; }
          .highlight { background: rgba(212,175,55,0.1); border-left: 3px solid #D4AF37; padding: 18px; margin: 18px 0; }
          .footer { text-align: center; margin-top: 50px; padding-top: 35px; border-top: 1px solid rgba(255,255,255,0.1); }
          .contact { font-size: 15px; margin-top: 18px; }
          ul { list-style: none; padding-left: 0; }
          ul li { padding: 6px 0; padding-left: 18px; position: relative; font-size: 13px; }
          ul li:before { content: '•'; color: #D4AF37; position: absolute; left: 0; }
          
          .print-btn {
            position: fixed;
            top: 20px;
            right: 20px;
            background: #D4AF37;
            color: #000;
            border: none;
            padding: 12px 24px;
            border-radius: 25px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 1px;
            cursor: pointer;
            z-index: 1000;
          }
          .print-btn:hover { background: #B8962E; }
          
          @media print {
            body { background: white; color: black; padding: 20px; }
            .gold { color: #8B7500; }
            .card { border-color: #ddd; background: #f9f9f9; }
            .cover { background: #050505; color: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .cover .gold { color: #D4AF37; }
            .print-btn { display: none; }
            .highlight { background: #f5f0e0; }
          }
        </style>
      </head>
      <body>
        <button class="print-btn" onclick="window.print()">Imprimir / Guardar PDF</button>
        
        <!-- COVER PAGE -->
        <div class="cover">
          <img src="${window.location.origin}/landing-assets/cristal.png" alt="Pont10 Logo" class="cover-logo" onerror="this.style.display='none'"/>
          <h1 class="serif cover-title gold">Besalú '26</h1>
          <p class="cover-subtitle">Presentación del Concepto</p>
          <h2 class="serif cover-main">Love & Thanks<br/><em>Water</em></h2>
          <p class="cover-desc">Un ritual transformador que conecta minimalismo, simbolismo y un vínculo emocional profundo con el territorio de Besalú.</p>
        </div>
        
        <!-- CONTENT -->
        <div class="content">
          <div class="section">
            <h2 class="serif">La Oportunidad</h2>
            <div class="highlight">
              <p style="font-size: 16px; opacity: 1;"><strong>El agua está en todas partes. Pero rara vez es memorable.</strong></p>
              <p>En la mayoría de restaurantes, el agua es solo funcional. ¿Y si el agua pudiera contar una historia?</p>
            </div>
          </div>

          <div class="section">
            <h2 class="serif">El Concepto</h2>
            <p>Inspirado por la belleza de las estructuras del agua y la idea de la gratitud, el diseño combina una fuerte conexión con el territorio con una experiencia moderna e interactiva.</p>
            
            <div class="grid">
              <div class="card">
                <h4 class="gold">Gratitud</h4>
                <p>El agua como vehículo de emociones positivas. Inspirado en la investigación de Masaru Emoto.</p>
              </div>
              <div class="card">
                <h4 class="gold">Minimalismo</h4>
                <p>Belleza oculta dentro de la simplicidad. Diseño puro que conecta con lo esencial.</p>
              </div>
              <div class="card">
                <h4 class="gold">Besalú</h4>
                <p>Conexión profunda con el territorio. El puente medieval como símbolo icónico.</p>
              </div>
              <div class="card">
                <h4 class="gold">Ritual</h4>
                <p>Transformar lo cotidiano en memorable. Un acto simple convertido en experiencia.</p>
              </div>
            </div>
          </div>

          <div class="section">
            <h2 class="serif">El Diseño</h2>
            <h3 class="gold">Diseño Frontal — Símbolo Cristal Icónico</h3>
            <p>El diseño frontal presenta el mensaje LOVE & THANKS WATER junto con un cristal geométrico. En el centro del cristal aparece el puente medieval de Besalú, creando una conexión simbólica entre el agua, la gratitud y el territorio.</p>
            
            <h3 class="gold" style="margin-top: 25px;">Diseño Trasero — Historia & Interacción</h3>
            <p>La parte trasera introduce la historia detrás del concepto e invita a los visitantes a interactuar a través de un código QR.</p>
            
            <ul style="margin-top: 15px;">
              <li>Escanea para descubrir la filosofía</li>
              <li>Deja un mensaje para el agua de Besalú</li>
              <li>Conecta con la comunidad global</li>
              <li>Participa en el ritual del agua</li>
            </ul>
          </div>

          <div class="section">
            <h2 class="serif">El Concepto de Experiencia</h2>
            <p>La botella transforma un acto simple — servir agua — en un ritual memorable.</p>
            
            <div class="grid">
              <div class="card">
                <h4><span class="gold">01</span> Objeto Icónico</h4>
                <p>Asociado a la identidad de Pont10</p>
              </div>
              <div class="card">
                <h4><span class="gold">02</span> Compartible</h4>
                <p>Elemento visual para turistas e invitados</p>
              </div>
              <div class="card">
                <h4><span class="gold">03</span> Entrada Digital</h4>
                <p>Código QR al ecosistema digital</p>
              </div>
              <div class="card">
                <h4><span class="gold">04</span> Souvenir</h4>
                <p>Futuro producto para visitantes</p>
              </div>
            </div>
          </div>

          <div class="section">
            <h2 class="serif">El Ritual</h2>
            <p><strong>Cuando la botella llega, la experiencia comienza:</strong></p>
            <div class="grid" style="grid-template-columns: repeat(3, 1fr); margin-top: 15px;">
              <div class="card" style="text-align: center;">
                <h4 class="gold">Curiosidad</h4>
                <p>Notan el símbolo único</p>
              </div>
              <div class="card" style="text-align: center;">
                <h4 class="gold">Conversación</h4>
                <p>Preguntan la historia</p>
              </div>
              <div class="card" style="text-align: center;">
                <h4 class="gold">Interacción</h4>
                <p>Escanean y descubren</p>
              </div>
            </div>
          </div>

          <div class="section">
            <h2 class="serif">Potencial Turístico</h2>
            <div class="highlight" style="text-align: center;">
              <p style="font-size: 22px; opacity: 1;" class="serif">Más de <span class="gold" style="font-size: 28px; font-weight: 600;">500.000</span> visitantes</p>
              <p>cada año en Besalú</p>
            </div>
            
            <div class="grid" style="grid-template-columns: repeat(3, 1fr);">
              <div class="card" style="text-align: center;">
                <h4 class="gold">Poder de Marca</h4>
                <p>Símbolo memorable</p>
              </div>
              <div class="card" style="text-align: center;">
                <h4 class="gold">Identidad Local</h4>
                <p>Conexión territorial</p>
              </div>
              <div class="card" style="text-align: center;">
                <h4 class="gold">Objeto Icónico</h4>
                <p>El Agua de Besalú</p>
              </div>
            </div>
          </div>

          <div class="footer">
            <h2 class="serif" style="font-size: 32px;">Besalú '26</h2>
            <p class="gold" style="margin-top: 12px; text-transform: uppercase; letter-spacing: 4px; font-size: 11px;">Únete al ritual de la gratitud</p>
            <p class="contact">
              <strong>Contacto:</strong> <a href="https://wa.me/34635011965" style="color: #D4AF37;">+34 635 011 965</a>
            </p>
            <p style="margin-top: 25px; font-size: 10px; opacity: 0.5;">© 2026 Pont10 — Love & Thanks Water Concept</p>
          </div>
        </div>
      </body>
      </html>
    `;
    
    // Open the PDF page served from the backend (mobile compatible)
    window.open('/api/landing-pdf', '_blank');
  };

  return (
    <div 
      className="font-sans text-white overflow-x-hidden"
      style={{ 
        fontFamily: '"Inter", system-ui, sans-serif',
        background: colors.black 
      }}
    >
      {/* Background Atmosphere */}
      <div className="fixed inset-0 -z-20 overflow-hidden pointer-events-none">
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.03, 0.06, 0.03]
          }}
          transition={{ duration: 15, repeat: Infinity }}
          className="absolute top-[-20%] left-[-20%] w-[80%] h-[80%] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(50,50,100,0.3) 0%, transparent 70%)', filter: 'blur(100px)' }}
        />
        <motion.div 
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.04, 0.08, 0.04]
          }}
          transition={{ duration: 12, repeat: Infinity, delay: 3 }}
          className="absolute bottom-[-20%] right-[-20%] w-[70%] h-[70%] rounded-full"
          style={{ background: `radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)`, filter: 'blur(120px)' }}
        />
      </div>

      {/* Navigation */}
      <motion.nav 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.5 }}
        className="fixed top-0 left-0 w-full z-50 px-6 md:px-12 py-6 flex justify-between items-center"
        style={{ background: 'linear-gradient(to bottom, rgba(5,5,5,0.9) 0%, transparent 100%)' }}
      >
        <div 
          className="text-xl md:text-2xl tracking-[0.4em] uppercase"
          style={{ fontFamily: '"Cinzel", serif', color: colors.gold }}
        >
          Pont10
        </div>
        <div className="hidden md:flex gap-10 text-[10px] uppercase tracking-[0.3em] font-medium text-white/40">
          <a href="#concepto" className="hover:text-[#D4AF37] transition-colors duration-300">Concepto</a>
          <a href="#diseno" className="hover:text-[#D4AF37] transition-colors duration-300">Diseño</a>
          <a href="#interaccion" className="hover:text-[#D4AF37] transition-colors duration-300">Interacción</a>
          <a href="#experiencia" className="hover:text-[#D4AF37] transition-colors duration-300">Experiencia</a>
        </div>
      </motion.nav>

      {/* ===== HERO SECTION ===== */}
      <Section className="relative">
        <motion.div 
          ref={heroRef}
          style={{ opacity: heroOpacity, scale: heroScale }}
          className="text-center z-10 max-w-5xl mx-auto"
        >
          {/* Crystal Logo - 50% larger */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="mb-10"
          >
            <img 
              src="/landing-assets/cristal.png" 
              alt="Crystal"
              className="w-32 h-32 md:w-44 md:h-44 mx-auto opacity-90"
              style={{ filter: 'drop-shadow(0 0 40px rgba(212,175,55,0.5))' }}
            />
          </motion.div>
          
          {/* Besalú '26 - Own line, larger */}
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-4xl sm:text-5xl md:text-6xl mb-4"
            style={{ fontFamily: '"Cinzel", serif', fontWeight: 300, color: colors.gold }}
          >
            Besalú '26
          </motion.h2>
          
          {/* Presentación del Concepto - Own line, larger */}
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="uppercase tracking-[0.3em] md:tracking-[0.5em] text-base sm:text-lg md:text-xl mb-10 font-medium opacity-70"
          >
            Presentación del Concepto
          </motion.p>
          
          {/* Main Title */}
          <motion.h1 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl mb-6 leading-[0.9] tracking-tight"
            style={{ fontFamily: '"Cinzel", serif', fontWeight: 300 }}
          >
            Love & Thanks
            <br />
            <span className="italic opacity-80" style={{ fontWeight: 200 }}>Water</span>
          </motion.h1>
          
          {/* Positioning Statement - Clear & Bold */}
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
            className="text-lg sm:text-xl md:text-2xl mb-8 tracking-[0.15em] uppercase font-medium"
            style={{ color: colors.gold }}
          >
            The Water Ritual of Besalú
          </motion.p>
          
          {/* Subtitle */}
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ delay: 1.4 }}
            className="max-w-2xl mx-auto text-base sm:text-lg md:text-xl font-light leading-relaxed tracking-wide px-6"
          >
            Un ritual transformador que conecta minimalismo, simbolismo y un vínculo emocional profundo con el territorio de Besalú.
          </motion.p>
        </motion.div>
        
        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5, duration: 1 }}
          className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2"
        >
          <div className="flex flex-col items-center gap-2">
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.3em] opacity-50">Desplaza para explorar</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 opacity-50" />
            </motion.div>
          </div>
        </motion.div>
      </Section>

      {/* ===== PROBLEM / OPPORTUNITY ===== */}
      <Section dark>
        <div className="max-w-4xl mx-auto text-center">
          <RevealText delay={0.1}>
            <p 
              className="text-[10px] uppercase tracking-[0.5em] mb-8 font-semibold"
              style={{ color: colors.gold }}
            >
              La Oportunidad
            </p>
          </RevealText>
          
          <RevealText delay={0.2}>
            <h2 
              className="text-3xl md:text-5xl lg:text-6xl mb-10 leading-tight"
              style={{ fontFamily: '"Cinzel", serif', fontWeight: 300 }}
            >
              El agua está en todas partes.
              <br />
              <span className="opacity-50">Pero rara vez es memorable.</span>
            </h2>
          </RevealText>
          
          <RevealText delay={0.3}>
            <p className="text-white/50 text-base md:text-xl font-light leading-relaxed max-w-2xl mx-auto">
              En la mayoría de restaurantes, el agua es solo funcional.
              <br />
              <span className="text-white/80 font-medium">¿Y si el agua pudiera contar una historia?</span>
            </p>
          </RevealText>
        </div>
      </Section>

      {/* ===== PONT10 SECTION - Why Us ===== */}
      <Section>
        <div className="max-w-5xl mx-auto text-center">
          <RevealText>
            <div className="space-y-8">
              {/* Pont10 Logo */}
              <motion.img 
                src="/landing-assets/pont10-logo.png" 
                alt="Pont10"
                className="h-48 sm:h-56 md:h-72 mx-auto"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              />
              <p 
                className="text-lg sm:text-xl md:text-2xl opacity-70 font-light max-w-2xl mx-auto leading-relaxed"
              >
                Un espacio gastronómico único en el corazón de Besalú que conecta
              </p>
            </div>
          </RevealText>
          
          {/* Three Pillars */}
          <div className="grid grid-cols-3 gap-4 md:gap-8 mt-12 md:mt-16 px-4">
            {[
              { word: "Territorio", desc: "Raíces en la historia medieval" },
              { word: "Naturaleza", desc: "Productos de km 0" },
              { word: "Experiencia", desc: "Momentos memorables" }
            ].map((item, i) => (
              <RevealText key={i} delay={0.15 * i}>
                <div className="text-center space-y-3 md:space-y-4">
                  <h3 
                    className="text-xl sm:text-2xl md:text-3xl lg:text-4xl"
                    style={{ fontFamily: '"Cinzel", serif', fontWeight: 400 }}
                  >
                    {item.word}
                  </h3>
                  <p className="text-sm md:text-base text-white/50 font-light">
                    {item.desc}
                  </p>
                </div>
              </RevealText>
            ))}
          </div>
          
          <RevealText delay={0.5}>
            <p className="mt-12 md:mt-16 text-sm md:text-base text-white/50 font-light italic max-w-xl mx-auto">
              "En Pont10, cada detalle cuenta una historia. El agua no es una excepción."
            </p>
          </RevealText>
        </div>
      </Section>

      {/* ===== CONCEPT SECTION ===== */}
      <Section id="concepto">
        <div className="grid md:grid-cols-2 gap-16 lg:gap-24 items-center max-w-7xl mx-auto w-full">
          {/* Left: Text Content */}
          <div className="space-y-12 px-4 order-2 md:order-1">
            <RevealText>
              <div className="space-y-6">
                <p 
                  className="text-xs md:text-[10px] uppercase tracking-[0.4em] md:tracking-[0.5em] font-semibold"
                  style={{ color: colors.gold }}
                >
                  La Visión
                </p>
                <h2 
                  className="text-5xl md:text-5xl lg:text-6xl leading-tight"
                  style={{ fontFamily: '"Cinzel", serif', fontWeight: 400 }}
                >
                  Minimalismo
                  <br />
                  <span className="opacity-70">&</span> Simbolismo
                </h2>
                <p className="text-white/50 text-base md:text-lg leading-relaxed font-light">
                  Inspirado por la belleza de las estructuras del agua y la idea de la gratitud,
                  el diseño combina una fuerte conexión con el territorio con una
                  experiencia moderna e interactiva.
                </p>
              </div>
            </RevealText>
            
            {/* Feature Cards Grid */}
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              {featureCards.map((item, i) => (
                <RevealText key={i} delay={0.1 * i}>
                  <GlassCard className="p-5 md:p-6" hover={false}>
                    <div className="space-y-3 md:space-y-4">
                      <div 
                        className="w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center"
                        style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.2)' }}
                      >
                        <item.icon className="w-5 h-5" style={{ color: colors.gold }} />
                      </div>
                      <h3 className="text-base md:text-sm uppercase tracking-[0.1em] md:tracking-[0.15em] font-semibold text-white">
                        {item.title}
                      </h3>
                      <p className="text-sm md:text-[11px] text-white/50 md:text-white/40 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </GlassCard>
                </RevealText>
              ))}
            </div>
          </div>
          
          {/* Right: Floating Crystal */}
          <motion.div 
            style={{ y: crystalY }} 
            className="flex justify-center order-1 md:order-2"
          >
            <FloatingCrystal className="w-64 h-64 md:w-[500px] md:h-[500px]" />
          </motion.div>
        </div>
      </Section>

      {/* ===== FRONT DESIGN SECTION ===== */}
      <Section id="diseno" dark>
        <div className="max-w-7xl w-full grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative order-2 md:order-1">
            <RevealText>
              <div className="relative aspect-[4/5] max-w-lg mx-auto">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.5 }}
                  className="relative h-full rounded-[40px] overflow-hidden shadow-2xl"
                  style={{ boxShadow: '0 25px 80px rgba(0,0,0,0.5)' }}
                >
                  <img 
                    src="/landing-assets/frontal.png" 
                    alt="Diseño Frontal - Love & Thanks Water Bottle" 
                    className="w-full h-full object-cover"
                  />
                  {/* Gradient Overlay */}
                  <div 
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)' }}
                  />
                  {/* Label */}
                  <div className="absolute bottom-8 left-8 right-8">
                    <p 
                      className="text-[10px] uppercase tracking-[0.4em] font-semibold mb-2"
                      style={{ color: colors.gold }}
                    >
                      Símbolo Icónico
                    </p>
                    <h3 
                      className="text-2xl md:text-3xl text-white"
                      style={{ fontFamily: '"Cinzel", serif' }}
                    >
                      El Diseño Frontal
                    </h3>
                  </div>
                </motion.div>
                
                {/* Decorative Ring */}
                <motion.div 
                  animate={{ rotate: 360 }}
                  transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                  className="absolute -top-8 -left-8 w-32 h-32 border border-[#D4AF37]/10 rounded-full -z-10"
                />
              </div>
            </RevealText>
          </div>

          {/* Text Content */}
          <div className="space-y-8 px-4 order-1 md:order-2">
            <RevealText>
              <div className="space-y-6">
                <h2 
                  className="text-5xl md:text-5xl leading-tight"
                  style={{ fontFamily: '"Cinzel", serif', fontWeight: 400 }}
                >
                  Símbolo Cristal
                  <br />
                  <span className="opacity-70">Icónico</span>
                </h2>
                <p className="text-white/50 text-base md:text-lg leading-relaxed font-light">
                  El diseño frontal presenta el mensaje{' '}
                  <span className="text-white font-medium">LOVE & THANKS WATER</span>{' '}
                  junto con un cristal geométrico. En el centro del cristal aparece el puente medieval de Besalú,
                  creando una conexión simbólica entre el agua, la gratitud y el territorio.
                </p>
              </div>
            </RevealText>
          </div>
        </div>
      </Section>

      {/* ===== INTERACTION SECTION ===== */}
      <Section id="interaccion">
        <div className="max-w-7xl w-full grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Content */}
          <div className="space-y-10 px-4">
            <RevealText>
              <div className="space-y-6">
                <p 
                  className="text-xs md:text-[10px] uppercase tracking-[0.4em] md:tracking-[0.5em] font-semibold"
                  style={{ color: colors.gold }}
                >
                  Ritual Digital
                </p>
                <h2 
                  className="text-5xl md:text-5xl leading-tight"
                  style={{ fontFamily: '"Cinzel", serif', fontWeight: 400 }}
                >
                  Historia &
                  <br />
                  <span className="opacity-70">Interacción</span>
                </h2>
                <p className="text-white/50 text-base md:text-lg leading-relaxed font-light">
                  La parte trasera introduce la historia detrás del concepto e invita a los visitantes
                  a interactuar a través de un código QR. Los huéspedes pueden explorar la experiencia Pont10,
                  descubrir la filosofía y participar dejando un mensaje.
                </p>
              </div>
            </RevealText>
            
            {/* Interaction Points */}
            <RevealText delay={0.2}>
              <div className="space-y-4 md:space-y-5">
                {interactionPoints.map((text, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * i }}
                    className="flex items-center gap-3 md:gap-4"
                  >
                    <div 
                      className="w-2.5 h-2.5 md:w-2 md:h-2 rounded-full flex-shrink-0"
                      style={{ background: colors.gold }}
                    />
                    <span className="text-sm md:text-xs uppercase tracking-[0.1em] md:tracking-[0.15em] text-white/70 md:text-white/60 font-medium">
                      {text}
                    </span>
                  </motion.div>
                ))}
              </div>
            </RevealText>
          </div>

          {/* Back Design Image */}
          <div className="relative">
            <RevealText>
              <div className="relative aspect-[4/5] max-w-lg mx-auto">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.5 }}
                  className="relative h-full rounded-[40px] overflow-hidden"
                  style={{ 
                    boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
                    border: '1px solid rgba(255,255,255,0.05)'
                  }}
                >
                  <img 
                    src="/landing-assets/trasera.png" 
                    alt="Diseño Trasero - Interacción QR" 
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </div>
            </RevealText>
          </div>
        </div>
      </Section>

      {/* ===== EXPERIENCE CONCEPT ===== */}
      <Section id="experiencia" dark>
        <div className="max-w-6xl mx-auto text-center space-y-16 md:space-y-20 w-full">
          <RevealText>
            <div className="space-y-6">
              <h2 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl italic"
                style={{ fontFamily: '"Cinzel", serif', fontWeight: 300 }}
              >
                El Concepto de Experiencia
              </h2>
              <p className="max-w-3xl mx-auto text-white/60 md:text-white/50 text-base md:text-lg font-light leading-relaxed px-4">
                La botella transforma un acto simple — servir agua — en un ritual memorable.
                Cuando la botella llega a la mesa, los huéspedes encuentran un símbolo, un mensaje y una historia.
              </p>
            </div>
          </RevealText>
          
          {/* Experience Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 px-4">
            {experienceSteps.map((item, i) => (
              <RevealText key={i} delay={0.1 * i}>
                <GlassCard className="p-6 md:p-8 lg:p-10 text-center h-full">
                  <div className="flex flex-col items-center justify-center space-y-4 md:space-y-6 h-full">
                    <div 
                      className="w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center"
                      style={{ background: 'rgba(212,175,55,0.08)' }}
                    >
                      <span 
                        className="text-base md:text-sm font-bold"
                        style={{ color: colors.gold }}
                      >
                        {item.num}
                      </span>
                    </div>
                    <div className="space-y-2 md:space-y-3">
                      <h4 className="text-base md:text-sm uppercase tracking-[0.1em] md:tracking-[0.15em] font-semibold text-white">
                        {item.title}
                      </h4>
                      <p className="text-sm md:text-[11px] text-white/50 md:text-white/40 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </GlassCard>
              </RevealText>
            ))}
          </div>
        </div>
      </Section>

      {/* ===== THE RITUAL SECTION ===== */}
      <Section>
        <div className="max-w-5xl mx-auto text-center space-y-12 md:space-y-16">
          <RevealText>
            <div className="space-y-4">
              <p 
                className="text-xs md:text-[10px] uppercase tracking-[0.4em] md:tracking-[0.5em] font-semibold"
                style={{ color: colors.gold }}
              >
                Un Ritual Simple
              </p>
              <h2 
                className="text-4xl md:text-5xl"
                style={{ fontFamily: '"Cinzel", serif', fontWeight: 400 }}
              >
                Cuando la botella llega, la experiencia comienza
              </h2>
            </div>
          </RevealText>
          
          {/* Ritual Steps */}
          <div className="grid md:grid-cols-3 gap-8 px-4">
            {[
              { 
                icon: Sparkles, 
                title: "Curiosidad", 
                desc: "Los huéspedes notan el símbolo único en la botella" 
              },
              { 
                icon: MessageSquare, 
                title: "Conversación", 
                desc: "Preguntan sobre la historia y el significado" 
              },
              { 
                icon: QrCode, 
                title: "Interacción", 
                desc: "Escanean el QR y descubren Pont10" 
              }
            ].map((step, i) => (
              <RevealText key={i} delay={0.15 * i}>
                <div className="text-center space-y-6">
                  <motion.div 
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-20 h-20 mx-auto rounded-3xl flex items-center justify-center"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)',
                      border: '1px solid rgba(212,175,55,0.2)'
                    }}
                  >
                    <step.icon className="w-8 h-8" style={{ color: colors.gold }} />
                  </motion.div>
                  <div className="space-y-2">
                    <h3 
                      className="text-xl"
                      style={{ fontFamily: '"Cinzel", serif' }}
                    >
                      {step.title}
                    </h3>
                    <p className="text-white/40 text-sm font-light">
                      {step.desc}
                    </p>
                  </div>
                </div>
              </RevealText>
            ))}
          </div>

          {/* CTA Button */}
          <RevealText delay={0.5}>
            <motion.a
              href="/lovewater"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="inline-block px-10 py-4 text-[11px] uppercase tracking-[0.25em] font-bold rounded-full transition-all duration-300 mt-8"
              style={{ 
                background: colors.gold, 
                color: colors.black 
              }}
            >
              Probar la experiencia
            </motion.a>
          </RevealText>
        </div>
      </Section>

      {/* ===== TOURISM POTENTIAL - Enhanced ===== */}
      <Section dark>
        <div className="max-w-5xl mx-auto text-center space-y-12 md:space-y-16">
          <RevealText>
            <div className="space-y-6">
              <p 
                className="text-xs md:text-[10px] uppercase tracking-[0.4em] md:tracking-[0.5em] font-semibold"
                style={{ color: colors.gold }}
              >
                Potencial Turístico
              </p>
              <h2 
                className="text-4xl sm:text-5xl md:text-6xl"
                style={{ fontFamily: '"Cinzel", serif', fontWeight: 300 }}
              >
                Más de <span style={{ color: colors.gold }}>500.000</span> visitantes
              </h2>
              <p className="text-white/60 md:text-white/50 text-lg font-light">
                cada año en Besalú
              </p>
            </div>
          </RevealText>
          
          {/* Impact Calculation */}
          <RevealText delay={0.2}>
            <GlassCard className="p-8 md:p-12 max-w-2xl mx-auto" hover={false}>
              <div className="space-y-6">
                <p className="text-white/60 text-sm md:text-base font-light">
                  Si solo el <span className="text-white font-semibold">1%</span> de los visitantes experimenta el ritual...
                </p>
                <div className="flex items-center justify-center gap-4">
                  <ArrowRight className="w-6 h-6 opacity-50" />
                  <span 
                    className="text-4xl md:text-5xl font-light"
                    style={{ fontFamily: '"Cinzel", serif', color: colors.gold }}
                  >
                    5.000
                  </span>
                  <span className="text-white/60 text-sm">personas/año</span>
                </div>
                <p className="text-white/40 text-xs md:text-sm italic">
                  Un proyecto escalable con potencial de crecimiento exponencial
                </p>
              </div>
            </GlassCard>
          </RevealText>
          
          <RevealText delay={0.3}>
            <div className="grid md:grid-cols-3 gap-4 md:gap-6 px-2 md:px-0">
              {[
                { icon: Users, label: "Poder de Marca", desc: "Un símbolo que la gente recuerda" },
                { icon: MapPin, label: "Identidad Local", desc: "Conexión profunda con el territorio" },
                { icon: Gift, label: "Objeto Icónico", desc: "El Agua de Besalú" }
              ].map((item, i) => (
                <GlassCard key={i} className="p-6 md:p-8" hover={false}>
                  <div className="flex flex-col items-center space-y-3 md:space-y-4">
                    <item.icon className="w-7 h-7 md:w-6 md:h-6" style={{ color: colors.gold }} />
                    <h4 className="text-base md:text-sm uppercase tracking-[0.1em] md:tracking-[0.15em] font-semibold">{item.label}</h4>
                    <p className="text-sm md:text-[11px] text-white/50 md:text-white/40 text-center">{item.desc}</p>
                  </div>
                </GlassCard>
              ))}
            </div>
          </RevealText>
        </div>
      </Section>

      {/* ===== FUTURE VISION ===== */}
      <Section>
        <div className="max-w-5xl mx-auto text-center space-y-12 md:space-y-16">
          <RevealText>
            <div className="space-y-6">
              <p 
                className="text-xs md:text-[10px] uppercase tracking-[0.4em] md:tracking-[0.5em] font-semibold"
                style={{ color: colors.gold }}
              >
                Visión de Futuro
              </p>
              <h2 
                className="text-4xl sm:text-5xl md:text-6xl"
                style={{ fontFamily: '"Cinzel", serif', fontWeight: 300 }}
              >
                Evolución
              </h2>
              <p className="text-white/50 text-base md:text-lg font-light max-w-2xl mx-auto">
                Love & Thanks Water no es solo una botella. Es el inicio de un ecosistema de experiencias.
              </p>
            </div>
          </RevealText>
          
          {/* Evolution Timeline */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 px-4">
            {[
              { 
                phase: "2026", 
                title: "Edición Anual", 
                desc: "Diseño exclusivo cada temporada",
                icon: Sparkles
              },
              { 
                phase: "2027", 
                title: "Souvenir", 
                desc: "Producto para llevarse a casa",
                icon: Gift
              },
              { 
                phase: "2028", 
                title: "Experiencia", 
                desc: "Ritual gastronómico completo",
                icon: Heart
              },
              { 
                phase: "2029", 
                title: "Territorio", 
                desc: "Producto de origen Besalú",
                icon: MapPin
              }
            ].map((item, i) => (
              <RevealText key={i} delay={0.1 * i}>
                <GlassCard className="p-5 md:p-6 h-full" hover={false}>
                  <div className="flex flex-col items-center text-center space-y-3 md:space-y-4 h-full">
                    <span 
                      className="text-xs font-bold tracking-wider"
                      style={{ color: colors.gold }}
                    >
                      {item.phase}
                    </span>
                    <item.icon className="w-6 h-6" style={{ color: colors.gold, opacity: 0.7 }} />
                    <h4 className="text-sm md:text-base font-semibold">{item.title}</h4>
                    <p className="text-xs text-white/40 font-light">{item.desc}</p>
                  </div>
                </GlassCard>
              </RevealText>
            ))}
          </div>
        </div>
      </Section>

      {/* ===== FINAL CTA ===== */}
      <Section>
        <div className="text-center space-y-12">
          <RevealText>
            <div className="space-y-6">
              <motion.div
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <img 
                  src="/landing-assets/cristal.png" 
                  alt="Crystal"
                  className="w-32 h-32 mx-auto mb-8"
                  style={{ filter: 'drop-shadow(0 0 40px rgba(212,175,55,0.4))' }}
                />
              </motion.div>
              <h2 
                className="text-5xl md:text-7xl lg:text-8xl"
                style={{ fontFamily: '"Cinzel", serif', fontWeight: 300 }}
              >
                Besalú '26
              </h2>
              <p 
                className="uppercase tracking-[0.5em] text-xs font-semibold"
                style={{ color: colors.gold }}
              >
                Únete al ritual de la gratitud
              </p>
            </div>
          </RevealText>
          
          <RevealText delay={0.3}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
              <motion.a
                href="https://wa.me/34635011965?text=Hola%2C%20me%20interesa%20el%20proyecto%20Love%20%26%20Thanks%20Water"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-10 py-4 text-[11px] uppercase tracking-[0.25em] font-bold rounded-full transition-all duration-300 text-center"
                style={{ 
                  background: colors.gold, 
                  color: colors.black 
                }}
              >
                Contactar
              </motion.a>
              <motion.button
                onClick={() => generatePDF()}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-10 py-4 text-[11px] uppercase tracking-[0.25em] font-bold rounded-full transition-all duration-300"
                style={{ 
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'transparent',
                  color: colors.white
                }}
              >
                Descargar Concepto
              </motion.button>
            </div>
          </RevealText>
        </div>
      </Section>

      {/* ===== FOOTER ===== */}
      <footer 
        className="py-20 px-8 text-center space-y-10"
        style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
      >
        <div 
          className="text-3xl tracking-[0.5em] uppercase"
          style={{ fontFamily: '"Cinzel", serif', color: colors.gold }}
        >
          Pont10
        </div>
        
        <div className="flex flex-wrap justify-center gap-x-12 gap-y-4 text-[10px] uppercase tracking-[0.3em] font-medium text-white/30">
          <a href="#" className="hover:text-[#D4AF37] transition-colors">Besalú</a>
          <a href="#" className="hover:text-[#D4AF37] transition-colors">Gratitud</a>
          <a href="#" className="hover:text-[#D4AF37] transition-colors">Minimalismo</a>
          <a href="#" className="hover:text-[#D4AF37] transition-colors">Ritual</a>
        </div>
        
        <div 
          className="pt-10 max-w-3xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6"
          style={{ borderTop: '1px solid rgba(255,255,255,0.03)' }}
        >
          <p className="text-[10px] text-white/20 uppercase tracking-[0.15em]">
            © 2026 Pont10 — Love & Thanks Water Concept
          </p>
          <div className="flex gap-8 text-[10px] text-white/20 uppercase tracking-[0.15em]">
            <a href="#" className="hover:text-white transition-colors">Privacidad</a>
            <a href="#" className="hover:text-white transition-colors">Términos</a>
          </div>
        </div>
      </footer>

      {/* Custom Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap');
        
        html {
          scroll-behavior: smooth;
        }
        
        ::-webkit-scrollbar {
          width: 6px;
        }
        
        ::-webkit-scrollbar-track {
          background: #050505;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #222;
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #D4AF37;
        }
        
        ::selection {
          background: rgba(212, 175, 55, 0.3);
          color: white;
        }
      `}</style>
    </div>
  );
}
